<template>
    <div class="p-6">
      <h1 class="text-3xl font-bold text-gray-900 dark:text-gray-50 mb-6 flex items-center">
        <button
          @click="$router.push('/agentgroup')"
          class="mr-4 bg-blue-600 text-white px-4 py-1.5 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition duration-200 text-sm font-medium flex items-center"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-4 w-4 mr-1"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fill-rule="evenodd"
              d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
              clip-rule="evenodd"
            />
          </svg>
          Back to Agent Groups
        </button>
        {{ isNewAgentGroup ? "Add Agent Group" : "Edit Agent Group" }}
        <div class="relative group">
        <button
          type="button"
          class="hover:bg-transparent bg-transparent ml-2"
          @click="startTutorial"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-circle-question-mark-icon lucide-circle-question-mark dark:text-gray-50 text-gray-900"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/></svg>
        </button>
          <span class="absolute top-full text-xs right-1/2 -translate-y-1/2 glass-card mb-1 px-1.5 py-0.5 text-xs text-white bg-gray-800 dark:bg-gray-700 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap">Tutorial</span>
        </div>
      </h1>


    
      <div v-if="isNewAgentGroup" id="progress-tracker" class="w-full dark:text-gray-50 flex justify-center mb-20">
        <div class="relative w-[80%] my-4"
        >
          <!-- Track Background -->
          <div class="absolute top-5 left-0 right-0 h-2 transform -translate-y-1/2 bg-gray-200 dark:bg-gray-700 rounded-full"></div>

          <!-- Progress Bar Foreground -->
          <div
            class="absolute top-5 left-0 h-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full transition-all duration-700 ease-out transform -translate-y-1/2"
            :style="{
              width:
                ((progress ) / (multiformTemplate.length - 1) * 100) + '%'
            }"
          >
          </div>

          <!-- Step Circles -->
          <div
            class="relative flex justify-between items-center w-full"
          >
            <template v-for="(step, index) in multiformTemplate" :key="index">
              <div class="flex flex-col items-center relative group">
                <div
                  class="relative w-10 h-10 flex items-center justify-center rounded-full text-sm font-semibold transition-all duration-500 transform group-hover:scale-110"
                  :class="{
                    'bg-gradient-to-br from-green-500 to-emerald-600 text-white shadow-lg': progress > index || multiformComplete,
                    'bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-2 border-gray-300 dark:border-gray-600': progress <= index && !multiformComplete
                  }"
                >
                  <svg
                    v-if="progress > index"
                    class="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/>
                  </svg>
                  <span v-else>{{ index + 1 }}</span>
                </div>
              
                <div
                  class="mt-10 font-bold absolute text-xs font-black text-center transition-all duration-300 whitespace-nowrap"
                  :class="{
                    'text-green-600 dark:text-green-400': progress >= index,
                    'text-gray-500 dark:text-gray-400': progress < index
                  }"
                >
                  {{ step }}
                </div>
              </div>
            </template>
          </div>
        </div>
      </div>
  
      <div v-if="loading" class="text-center py-4">
        <p class="text-gray-600">Loading...</p>
      </div>

      <HowToAgentGroup v-if="showHowToPage"
        :show-how-to-page="showHowToPage"
        :external-navigation="true"
        @show-how-to-close="handleShowHowToPage"
      />
  
      <div
        v-else-if="error"
        class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative"
        role="alert"
      >
        <strong class="font-bold">Error!</strong>
        <span class="block sm:inline">{{ error }}</span>
      </div>
  
      <form v-if="showGroup" @submit.prevent="saveAgentGroup" class="space-y-6 p-6 glass-card">
        <div>
          <label
            for="max_concurrent_updates"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Name</label
          >
          <input
            type="text"
            id="name"
            v-model="agentgroup.name"
            @focus="clearFieldError('name')"
            placeholder="Group 9"
            :class="[ 'w-full border border-gray-300 rounded px-3 py-1.5 focus:outline-none focus:ring-2', fieldErrors['name']
                  ? 'ring-2 ring-red-500  focus:ring-red-500'
                  : 'focus:ring-blue-500'
            ]"
          />
          <FieldError :message="fieldErrors['name']"/>
        </div>


        <div>
          <label
            for="organization"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Organisation</label
          >
          <GlassSelect
            id="organization"
            v-model="agentgroup.organization"
            @focus="clearFieldError('organization')"
            :options="orgs"
            placeholder="Select an organisation"
            :error="!!fieldErrors['organization']"
          />
          <FieldError :message="fieldErrors['organization']"/>
        </div>

        <div v-if="isNewAgentGroup">
          <label
            for="os_type"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Operating System</label
          >
          <GlassSelect
            id="os_type"
            v-model="selectedOS"
            @focus="clearFieldError('os_type')"
            :options="os_type_options"
            valueKey="key"
            labelKey="label"
            placeholder="Select an operating system"
            :error="!!fieldErrors['os_type']"
          />
          <FieldError :message="fieldErrors['os_type']"/>
        </div>

        <div v-if="isNewAgentGroup && selectedOS === 'Windows'">
          <label
            for="space_type"
            class="block text-sm font-medium text-gray-700 dark:text-white"
            >Space Type</label
          >
          <GlassSelect
            id="space_type"
            v-model="selectedSpaceType"
            @focus="clearFieldError('space_type')"
            :options="space_type_options"
            valueKey="key"
            labelKey="label"
            placeholder="Select a space type"
            :error="!!fieldErrors['space_type']"
          />
          <FieldError :message="fieldErrors['space_type']"/>
        </div>

        <FieldError :message="fieldErrors['non_field_errors']"/>
        
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="$router.push({ name: 'AgentGroupView' })"
            class="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Cancel
          </button>
          <button v-if="isStaff || canChangeAgentGroup || canAddAgentGroup"
            id="submit_group"
            type="submit"
            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            {{ isNewAgentGroup ? "Create Agent Group" : "Save Changes" }}
          </button>
        </div>
      </form>

      <div v-else-if="showCollector">
        <div class="flex items-center gap-3 glass-card-lite p-4 mb-4 rounded-lg border-l-4 border-indigo-500">
          <svg class="shrink-0 h-5 w-5 text-indigo-500 dark:text-indigo-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-sm flex-1 text-gray-700 dark:text-gray-300">
            If an organisation-wide Collector config exists, this group can inherit it instead of having its own.
          </p>
          <button
            type="button"
            @click="skipCollector"
            class="shrink-0 whitespace-nowrap px-4 py-2 border border-indigo-500 rounded-md shadow-sm text-sm font-medium text-indigo-600 dark:text-indigo-300 hover:bg-indigo-600 hover:text-white dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Skip, and use global Collector config
          </button>
        </div>

        <ConfigWorkSpaceDetails
          ref="configWorkspace"
          :configType="'collectorconfig'"
          :isMultiSubmit="true"
          :multiSubmitGroup="agentgroup?.id"
          :multiSubmitOrg="agentgroup?.organization"
          :multiSubmitOS="selectedOS"
          :multiSubmitSpaceType="selectedSpaceType"
          @collector-submitted = handleCollector
        />
      </div>

      <div v-else-if="showAlloy">
        <div class="flex items-center gap-3 glass-card-lite p-4 mb-4 rounded-lg border-l-4 border-indigo-500">
          <svg class="shrink-0 h-5 w-5 text-indigo-500 dark:text-indigo-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p class="text-sm flex-1 text-gray-700 dark:text-gray-300">
            If an organisation-wide Alloy config exists, this group can inherit it instead of having its own.
          </p>
          <button
            type="button"
            @click="skipAlloy"
            class="shrink-0 whitespace-nowrap px-4 py-2 border border-indigo-500 rounded-md shadow-sm text-sm font-medium text-indigo-600 dark:text-indigo-300 hover:bg-indigo-600 hover:text-white dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Skip, and use global Alloy config
          </button>
        </div>

        <ConfigWorkSpaceDetails
          ref="configAlloy"
          :configType="'alloyconfig'"
          :isMultiSubmit="true"
          :multiSubmitGroup="agentgroup?.id"
          :multiSubmitOrg="agentgroup?.organization"
          :multiSubmitOS="selectedOS"
          :multiSubmitSpaceType="selectedSpaceType"
          @collector-submitted = handleAlloy
        />
      </div>

      <div 
        class="flex justify-center mt-10"
         v-if="multiformComplete"
      >
        <div
          class="text-center p-8 glass-card dark:text-gray-50 font-semibold rounded-lg w-[80%]"
          >
          <svg xmlns="http://www.w3.org/2000/svg"
            width="30" height="30"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="h-10 w-10 mr-2 inline-block text-green-600 lucide lucide-thumbs-up-icon lucide-thumbs-up">
            <path d="M7 10v12"/>
            <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H4a2 2 0 0 1-2-2v-8a2 2 0 0 1 2-2h2.76a2 2 0 0 0 1.79-1.11L12 2a3.13 3.13 0 0 1 3 3.88Z"/>
          </svg>
          <p class="text-gray-800 dark:text-gray-50">Group {{agentgroup.name}}  -  Created</p>
          <div 
            class="flex items-center justify-center"
            >
            <div class="mt-6 flex space-x-3">
              <button
                @click="$router.push('/agentgroup')"
                class="text-white bg-blue-600 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-sm px-5 py-2.5 text-center me-2 mb-2"
              >
                Back to Agent Groups
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>

  import { createAgentGroup, getAgentGroups, updateAgentGroup } from "@/services/agentGroupService";
  import { getOrgs } from "@/services/wsOrgService";
  import GlassSelect from "@/components/GlassSelect.vue";
  import { useStore } from "@/store";
  import FieldError from "@/components/FieldError.vue";
  import ConfigWorkSpaceDetails from "./ConfigWorkSpaceDetails.vue";
  import HowToAgentGroup from "./HowToAgentGroup.vue";
  import { useTutorial } from "@/composables/tutorial";

  export default {
    name: "AgentGroupDetails",
    setup() {
      const { start } = useTutorial();
      return { _start: start };
    },
    emits: ['a-submitted'],
    components: {
      FieldError,
      ConfigWorkSpaceDetails,
      HowToAgentGroup,
      GlassSelect,
    },
    data() {
      return {
        agentgroup: {
          name: "",
          organization: null,
        },
        selectedSpaceType: "",
        space_type_options: [
          { key: "serverspace", label: "Serverspace" },
          { key: "workspace", label: "Workspace" },
        ],
        // OS type — used only to scope the collector config step, not persisted on the group.
        selectedOS: "",
        os_type_options: [
          { key: "Windows", label: "Windows" },
          { key: "Linux", label: "Linux" },
        ],
        showHowToPage: false,
        loading: false,
        error: null,
        fieldErrors: {},
        isNewAgentGroup: false,
        orgs: [],
        canChangeAgentGroup: false,
        canAddAgentGroup: false,

        // Multi Step Config
        showCollector: false,
        showAlloy: false,
        showGroup: true,
        progress: 0,
        multiformComplete: false,
        steps: [
          {
            element: '#progress-tracker',
            action: 'display',
            onlyNew: true,
            message: 'Track your progress through the form.'
          },
          {
            element: '#name',
            action: 'type',
            value: 'HR-1',
            message: 'Enter a name for the agent group.'
          },
          {
            element: '#organization',
            action: 'select',
            value: 1,
            message: 'Select the organization this group belongs to.'
          },
          {
            element: '#submit_group',
            action: 'display',
            message: 'Submit to create the agent group.'
          }
        ],
      };
    },
    watch : {
      user: {
        handler(user) {
          if (user) {
            this.checkPermissions();
          }
        },
        immediate: true
      },
      // Linux only supports serverspace; lock it and make Windows choose explicitly.
      selectedOS(os) {
        this.selectedSpaceType = os === 'Linux' ? 'serverspace' : '';
      },
    },
    computed: {
      needsAlloy() {
        return this.selectedOS === 'Linux' ||
          (this.selectedOS === 'Windows' && ['serverspace', 'workspace'].includes(this.selectedSpaceType));
      },
      multiformTemplate() {
        return [
          'Create Group',
          'Add Collector Config',
          ...(this.needsAlloy ? ['Add Alloy Config'] : []),
          'Complete',
        ];
      },
      isStaff(){
        return useStore.isStaff();
      },
      user() {
        return useStore.getUser();
      }, 
      formattedAgentGroup() {
        return {
          ...this.agentgroup
        };
      },
    },
    async created() {
      const agentgroupId = this.$route.params.id;
      this.checkPermissions();
      if (agentgroupId === "new") {
        this.isNewAgentGroup = true;
        await this.fetchOrg()
      } else {
        await this.fetchAgentGroup(agentgroupId);
        await this.fetchOrg()
      }
    },
    methods: {
      handleShowHowToPage(value){
        this.showHowToPage = value;
      },
      clearFieldError(fieldName){
        if (this.fieldErrors[fieldName]) {
          delete this.fieldErrors[fieldName];
          delete this.fieldErrors['non_field_errors'];
        }
      },
      handleCollector(){
        this.showCollector = false;
        this.progress += 1;
        if (this.needsAlloy) {
          this.showAlloy = true;
        } else {
          this.multiformComplete = true;
        }
      },
      handleAlloy(){
        this.showAlloy = false;
        this.progress += 1;
        this.multiformComplete = true;
      },
      // Skipping a step creates no group-scoped config; agents fall back to
      // the organisation-wide one at checkin.
      skipCollector(){
        this.handleCollector();
      },
      skipAlloy(){
        this.handleAlloy();
      },
      checkPermissions(){
        this.canChangeAgentGroup = useStore.hasPermission("Can change agent groups");
        this.canAddAgentGroup = useStore.hasPermission("Can add agent groups");
      },
      async fetchOrg(){
        try {
            this.orgs = await getOrgs();
        } catch (error) {
            console.error(error); 
        }
      },
      async fetchAgentGroup(agentgroupId) {
        this.loading = true;
        this.error = null;
        try {
          
          const agentGroups = await getAgentGroups();
          this.agentgroup = agentGroups.find(item => item.id == agentgroupId);

        } catch (error) {
          console.error("Error fetching organization:", error);
          this.error =
            "An error occurred while fetching organization details. Please try again later.";
        } finally {
          this.loading = false;
        }
      },
      async saveAgentGroup() {

        this.validateAgentGroupData();
        if (Object.keys(this.fieldErrors).length > 0) return;

        this.loading = true;
        this.error = null;
        
        try {
          const agentgroupData = this.formattedAgentGroup;
          if (this.isNewAgentGroup) {
            const response = await createAgentGroup(agentgroupData);

            this.progress += 1;
            this.showGroup = false;
            this.showCollector = true;
            this.agentgroup.id = response?.id;


          } else {
            agentgroupData.organization = this.agentgroup.organization;
            await updateAgentGroup(this.agentgroup.id, agentgroupData);
            this.$router.push({ name: "AgentGroupView" });
          }
          
        } catch (error) {
          this.fieldErrors = {};
          const errData = error?.response?.data;
          if (errData) {
            for (const key in errData) {
              const value = errData[key];
  
              if (Array.isArray(value)) {
                this.fieldErrors[key] = value[0];
              } 
              else if (typeof value === 'object' && value !== null) {
                const nestedFirstKey = Object.keys(value)[0];
                const nestedMessages = value[nestedFirstKey];
  
                if (Array.isArray(nestedMessages)) {
                  this.fieldErrors[key] = nestedMessages[0];
                }
              }
            }
          }
  
          console.error("Error saving organization:", error);
        } finally {
          this.loading = false;
        }
      },
      validateAgentGroupData (){
        this.fieldErrors = {};
        if (this.agentgroup.name === ""){
          this.fieldErrors['name'] = "The name field cannot be empty"
        }
        if (this.agentgroup.organization === null){
          this.fieldErrors['organization'] = "Please select an organisation for this group"
        }
        if (this.isNewAgentGroup && this.selectedOS === ""){
          this.fieldErrors['os_type'] = "Please select an operating system for this group"
        }
        if (this.isNewAgentGroup && this.selectedOS === "Windows" && this.selectedSpaceType === ""){
          this.fieldErrors['space_type'] = "Please select a space type for this group"
        }
      },
      async startTutorial() {
        if (this.showCollector && this.$refs.configWorkspace) {
          await this.$refs.configWorkspace.startTutorial();
          return;
        }
        if (this.showAlloy && this.$refs.configAlloy) {
          await this.$refs.configAlloy.startTutorial();
          return;
        }

        const originalOrgs = [...this.orgs];
        const originalAgentGroup = { ...this.agentgroup };

        const steps = this.steps.filter(step => !step.onlyNew || this.isNewAgentGroup);

        this.orgs = [{ id: 1, name: 'Tutorial Organisation' }];

        await this._start(steps, () => {
          this.orgs = originalOrgs;
          this.agentgroup = { ...originalAgentGroup };
        });
      }
    },
  };
  </script>
  